import { apiBackofficeReportsMs } from "../api-backoffice-reports-ms";
import { z } from "zod";
import { apiLegacy } from "../api-legacy";
import { SpacesResponseApi } from "@/features/private-room/bookings/adapters/get-spaces/get-spaces.types";
import { apiWobaBackend } from "../api-woba-backend";
import { apiSearchMs } from "../api-search-ms";

const searchSpacesResponseSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
  }),
);

const spaceResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  vanishName: z.string(),
  workHours: z.array(z.string().nullable()),
  country: z.string(),
  enabled: z.boolean(),
  timezone: z.string(),
  city: z.string(),
  type: z.number(),
  isMeetingRoomPartnership: z.boolean(),
  admUsersCoworking: z
    .array(
      z.object({
        email: z.string().nullable().optional(),
        firstName: z.string(),
        id: z.number(),
        lastName: z.string(),
      }),
    )
    .optional(),
  address: z.string(),
  admMail: z.string().nullable(),
  neighborhood: z.string().nullable(),
});

const spaceWithoutPrivateOfficesSchema = z.object({
  spaceId: z.number(),
  name: z.string(),
  address: z.string(),
  neighborhood: z.string().nullable(),
  city: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  enabled: z.boolean(),
  type: z.number(),
  admMail: z.string().nullable().optional(),
  hasMeetingRoomPartnership: z.boolean().optional(),
  privateOfficesCount: z.number().optional(),
});

const spacesWithoutPrivateOfficesResponseSchema = z.object({
  total: z.number(),
  results: z.array(spaceWithoutPrivateOfficesSchema),
});

const spaceWithCoordinatesSchema = z.object({
  id: z.number(),
  name: z.string(),
  vanishName: z.string(),
  country: z.string(),
  city: z.string(),
  address: z.string(),
  neighborhood: z.string().nullable(),
  enabled: z.boolean(),
  timezone: z.string(),
  type: z.number(),
  latitude: z.number(),
  longitude: z.number(),
  tour_video: z.string().nullable().optional(),
});

const spacesListResponseSchema = z.object({
  data: z.array(spaceWithCoordinatesSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

export const spaceService = {
  async searchSpaces({
    term,
  }: {
    term: string;
  }): Promise<z.infer<typeof searchSpacesResponseSchema>> {
    try {
      const searchParams = new URLSearchParams();
      if (term) {
        searchParams.append("term", term);
      }
      const response = await apiBackofficeReportsMs.get(
        `/spaces?${searchParams}`,
      );

      const parsedData = searchSpacesResponseSchema.parse(response.data);

      return parsedData;
    } catch (error) {
      //TODO: Implement error tracking
      console.error(error);
      return [];
    }
  },

  async getSpaces(simplelist?: boolean) {
    const response = await apiLegacy.get<SpacesResponseApi["data"]>(
      `/v0/spaces?include_floors=true${simplelist ? "&type=simplelist" : ""}`,
    );

    const data = response.data;

    const spaces =
      data?._embedded?.spaces.map((item) => {
        return {
          id: item.id,
          name: item.name,
        };
      }) ?? [];

    return {
      data: spaces,
      complete: data?._embedded?.spaces,
    };
  },

  async getSpaceById({
    spaceId,
  }: {
    spaceId: number | string;
  }): Promise<z.infer<typeof spaceResponseSchema>> {
    const response = await apiWobaBackend.get(`/spaces/${spaceId}`);

    const parsedData = spaceResponseSchema.parse(response.data);

    return parsedData;
  },

  async getSearchSpaces({ term }: { term: string }) {
    const response = await apiSearchMs.get(
      `/places/search?limit=3&language=pt-BR&term=${encodeURIComponent(term)}`,
    );

    return response.data;
  },

  async getSpaceByLocation(term: string) {
    const response = await apiSearchMs.get(
      `/places/search?limit=3&language=pt-BR&term=${encodeURIComponent(
        term,
      )}&location=true`,
    );

    return response.data;
  },

  async getSpacesWithoutPrivateOffices(params?: {
    latitude?: number;
    longitude?: number;
    distance?: number;
    excludeSpaceIds?: number[];
  }): Promise<z.infer<typeof spacesWithoutPrivateOfficesResponseSchema>> {
    const searchParams = new URLSearchParams();
    if (params?.latitude !== undefined) {
      searchParams.append("latitude", params.latitude.toString());
    }
    if (params?.longitude !== undefined) {
      searchParams.append("longitude", params.longitude.toString());
    }
    if (params?.distance !== undefined) {
      searchParams.append("distance", params.distance.toString());
    }
    if (params?.excludeSpaceIds && params.excludeSpaceIds.length > 0) {
      searchParams.append("excludeSpaceIds", params.excludeSpaceIds.join(","));
    }

    const url = `/spaces/without-private-offices${searchParams.toString() ? `?${searchParams}` : ""}`;
    const response = await apiWobaBackend.get(url);

    const parsedData = spacesWithoutPrivateOfficesResponseSchema.parse(
      response.data,
    );

    return parsedData;
  },

  async getSpacesByIds(
    ids: number[],
  ): Promise<z.infer<typeof spacesListResponseSchema>> {
    if (ids.length === 0) {
      return {
        data: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
      };
    }

    const idsParam = ids.join(",");
    const response = await apiWobaBackend.get(
      `/spaces?ids=${idsParam}&limit=${ids.length}`,
    );

    const parsedData = spacesListResponseSchema.parse(response.data);

    return parsedData;
  },
};
